import { useCallback, useEffect, useMemo } from "react"

import { App, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { useTitleSectionAction } from "../../../Components/TitleSection/TitleSectionContext"
import { toLookupFilterParams } from "../api/params"
import { useReceiptBusinessId } from "../hooks/useReceiptBusinessId"
import { lookupRequestScope } from "../utils/lookupRequestScope"
import { CaseLookup } from "./components/CaseLookup"
import { CaseStateOverlay } from "./components/CaseStateOverlay"
import HeaderActions from "./components/HeaderActions"
import { KpiCards } from "./components/KpiCards"
import { ProgramCards } from "./components/ProgramCards"
import { CE_PAGE_STATE } from "./constants/page-states"
import {
  clearFilters,
  selectCompellingEvidence,
  setProp,
  setSelectedProgram
} from "./redux/compelling-evidence.slice"
import {
  getCompellingEvidenceCases,
  getCompellingEvidenceExport,
  getCompellingEvidenceKpi,
  getCompellingEvidenceState
} from "./redux/compelling-evidence.thunks"

import styles from "./styles.module.less"

const ReceiptCompellingEvidencePage = () => {
  const dispatch = useDispatch()
  const businessId = useReceiptBusinessId()
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const { setAction } = useTitleSectionAction()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const isDemo = useSelector((s) => s.auth?.userInfo?.is_demo) || false
  const { pageState, selectedProgram, filters } = useSelector(selectCompellingEvidence)

  const isActive = pageState === CE_PAGE_STATE.ACTIVE

  // As on Receipt: the filter bar's Program, when set, is more specific than the selected card,
  // and the KPI cards answer about the same rows as the table.
  const filterParams = useMemo(() => toLookupFilterParams(filters), [filters])
  const programScope = filterParams?.program || selectedProgram || "all"

  useEffect(() => {
    const request = dispatch(getCompellingEvidenceState(businessId))
    return () => request.abort()
  }, [dispatch, businessId])

  useEffect(() => {
    if (!isActive) return
    const request = dispatch(getCompellingEvidenceKpi({ businessId, program: programScope }))
    return () => request.abort()
  }, [dispatch, businessId, programScope, isActive])

  const handleGetCases = useCallback(
    (params) =>
      dispatch(
        getCompellingEvidenceCases({
          ...lookupRequestScope({ businessId }),
          ...filterParams,
          program: programScope,
          ...params
        })
      ),
    [dispatch, businessId, filterParams, programScope]
  )

  const handleExport = useCallback(async () => {
    try {
      const res = await dispatch(
        getCompellingEvidenceExport({
          ...lookupRequestScope({ businessId }),
          ...filterParams,
          program: programScope
        })
      ).unwrap()

      notification.success({ message: t("Exporting"), description: res?.message })
    } catch {
      /* error handled in thunk */
    }
  }, [dispatch, businessId, filterParams, programScope, notification, t])

  // Re-registered when what it shows changes: the header action is held in context, so a stale
  // element would keep the first render's values forever.
  useEffect(() => {
    setAction(
      <HeaderActions
        isMobile={isMobile}
        isActive={isActive}
        isDemo={isDemo}
        onExport={handleExport}
      />
    )
    return () => setAction(null)
  }, [setAction, isMobile, isActive, isDemo, handleExport])

  const handleSelectProgram = useCallback((id) => dispatch(setSelectedProgram(id)), [dispatch])

  const handleChangeFilter = useCallback(
    (patch, type) => {
      const key = type === "changeVisible" ? "visibleFilters" : "filters"
      dispatch(setProp({ key, value: patch }))
    },
    [dispatch]
  )

  const handleClearFilters = useCallback(() => dispatch(clearFilters()), [dispatch])

  const handleChangeColumns = useCallback(
    (cols) => dispatch(setProp({ key: "columns", value: cols })),
    [dispatch]
  )

  const handleChangeViewMode = useCallback(
    (mode) => dispatch(setProp({ key: "viewMode", value: mode })),
    [dispatch]
  )

  return (
    <Flex vertical gap={24} className={styles.page}>
      <ProgramCards selectedProgram={selectedProgram} onSelect={handleSelectProgram} />
      <KpiCards />
      {isActive ? (
        <CaseLookup
          isMobile={isMobile}
          isDemo={isDemo}
          onExport={handleExport}
          onChangeFilter={handleChangeFilter}
          onClearFilters={handleClearFilters}
          onChangeColumns={handleChangeColumns}
          onChangeViewMode={handleChangeViewMode}
          onGetCases={handleGetCases}
        />
      ) : (
        <CaseStateOverlay />
      )}
    </Flex>
  )
}

export default ReceiptCompellingEvidencePage
